import React, { useEffect, useMemo, useState } from 'react';
import { nudgeDana } from '@/features/shared/dana/danaNudge';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  ChevronRight,
  Wallet,
  ClipboardCheck,
  Banknote,
  CheckCircle2,
  FileEdit,
  LayoutGrid,
  List,
  RefreshCw,
} from 'lucide-react';
import { getApiErrorMessage } from '@/lib/apiError';
import { PageBoundary } from '@/features/shared/components/PageBoundary';
import { GroupPayrollOverview } from '../components/GroupPayrollOverview';
import { listVisibleCompanies, type OverviewCompany } from '../api/groupOverview';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Skeleton } from '@/features/shared/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/features/shared/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/features/shared/components/ui/select';
import { cn } from '@/lib/utils';
import { payrollApi } from '../api/payrollApi';
import type { PayrollRun, RunState } from '../types';
import { RUN_STATE_META } from '../lib/state';
import { formatMoney, COUNTRY_FLAG, COUNTRY_LABEL } from '../lib/format';
import { StatusChip } from '../components/StatusChip';
import { StatCard } from '../components/StatCard';
import { NewRunDialog } from '../components/NewRunDialog';

type Filter = 'all' | RunState;

const FILTERS: { value: Filter; labelKey: string }[] = [
  { value: 'all', labelKey: 'runs.filterAll' },
  { value: 'draft', labelKey: 'runs.filterDraft' },
  { value: 'validated', labelKey: 'runs.filterValidated' },
  { value: 'approved', labelKey: 'runs.filterApproved' },
  { value: 'locked', labelKey: 'runs.filterLocked' },
  { value: 'paid', labelKey: 'runs.filterPaid' },
  { value: 'cancelled', labelKey: 'runs.filterCancelled' },
];

export const PayrollRunsPage: React.FC = () => {
  const { t } = useTranslation('payroll');
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const base = pathname.replace(/\/$/, '');
  const [runs, setRuns] = useState<PayrollRun[]>([]);
  const [loading, setLoading] = useState(true);
  // A failed load is an ERROR, not "no payroll runs yet" — the empty state would tell an
  // approver there is nothing to approve.
  const [loadError, setLoadError] = useState<string | null>(null);
  // Companies the person can see (GET /legal-entities is reach-filtered server-side). null
  // until loaded or if it failed — the filter then falls back to companies seen in runs.
  const [companies, setCompanies] = useState<OverviewCompany[] | null>(null);
  const [view, setView] = useState<'runs' | 'overview'>('runs');
  const [filter, setFilter] = useState<Filter>('all');
  const [entityFilter, setEntityFilter] = useState<string>('all');
  const [periodFilter, setPeriodFilter] = useState<string>('all');
  const [newRunOpen, setNewRunOpen] = useState(false);

  const loadRuns = () => {
    setLoading(true);
    setLoadError(null);
    payrollApi
      .listRuns()
      .then((r) => { setRuns(r); if ((r?.length ?? 0) === 0) nudgeDana({ key: 'payroll_runs', message: 'No payroll runs yet — want help creating your first one?', question: 'How do I create a payroll run?' }); })
      .catch((e) => { setRuns([]); setLoadError(getApiErrorMessage(e, t('runs.loadError', { defaultValue: 'Payroll runs could not be loaded.' }))); })
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    loadRuns();
    listVisibleCompanies().then(setCompanies).catch(() => setCompanies(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stats = useMemo(() => {
    const by = (s: RunState) => runs.filter((r) => r.state === s).length;
    return {
      total: runs.length,
      // Draft = created but not yet validated. Given a tenant whose runs are all
      // drafts, the state cards would otherwise all read 0 and the board would
      // look empty/untruthful (US-11) — so drafts get their own tile.
      draft: by('draft'),
      // Only `validated` truly awaits the HR→finance→final approval chain.
      // `approved` = chain complete, awaiting LOCK — counted separately (US-11).
      awaiting: by('validated'),
      approved: by('approved'),
      ready: by('locked'),
      paid: by('paid'),
    };
  }, [runs]);

  // Company filter: the companies the person can see (reach-filtered server-side), so a
  // company with no runs yet is still selectable. Falls back to the companies present in
  // the runs when that list couldn't load.
  const entities = useMemo(() => {
    const m = new Map<string, string>();
    if (companies) companies.forEach((c) => m.set(c.id, c.name));
    else runs.forEach((r) => { if (r.legalEntityId) m.set(r.legalEntityId, r.legalEntityName); });
    return [...m.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [runs, companies]);
  const multiCompany = entities.length > 1;
  const periods = useMemo(() => {
    const seen = new Map<string, string>(); // label → periodStart (for chronological sort)
    runs.forEach((r) => { if (!seen.has(r.periodLabel)) seen.set(r.periodLabel, r.periodStart); });
    return [...seen.entries()].sort((a, b) => b[1].localeCompare(a[1])).map(([label]) => label);
  }, [runs]);

  const filtersActive = filter !== 'all' || entityFilter !== 'all' || periodFilter !== 'all';
  const visible = useMemo(
    () =>
      runs.filter(
        (r) =>
          (filter === 'all' || r.state === filter) &&
          (entityFilter === 'all' || r.legalEntityId === entityFilter) &&
          (periodFilter === 'all' || r.periodLabel === periodFilter),
      ),
    [runs, filter, entityFilter, periodFilter],
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-border pb-6">
        <div>
          <h1 className="text-h1 text-charcoal">{t('runs.title')}</h1>
          <p className="text-muted-foreground mt-1">
            {t('runs.description')}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {multiCompany && (
            <div className="inline-flex rounded-lg border border-border p-0.5" role="tablist" aria-label="View">
              <button
                type="button"
                role="tab"
                aria-selected={view === 'runs'}
                onClick={() => setView('runs')}
                className={cn('inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium',
                  view === 'runs' ? 'bg-charcoal text-white' : 'text-muted-foreground hover:bg-muted')}
              >
                <List className="h-4 w-4" /> {t('runs.viewRuns', { defaultValue: 'Runs' })}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={view === 'overview'}
                onClick={() => setView('overview')}
                className={cn('inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium',
                  view === 'overview' ? 'bg-charcoal text-white' : 'text-muted-foreground hover:bg-muted')}
              >
                <LayoutGrid className="h-4 w-4" /> {t('runs.viewOverview', { defaultValue: 'Group overview' })}
              </button>
            </div>
          )}
          <Button className="gap-2" onClick={() => setNewRunOpen(true)}>
            <Plus className="h-4 w-4" />
            {t('runs.newButton')}
          </Button>
        </div>
      </div>

      <NewRunDialog
        open={newRunOpen}
        onOpenChange={setNewRunOpen}
        onCreated={(run) => navigate(`${base}/runs/${run.id}`)}
      />

      {view === 'overview' && multiCompany ? (
        <PageBoundary compact title="Group overview">
          <GroupPayrollOverview onOpenRun={(id) => navigate(`${base}/runs/${id}`)} />
        </PageBoundary>
      ) : (
      <>
      {loadError && (
        <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-danger-soft p-4 text-sm text-danger-strong">
          <span>{loadError}</span>
          <Button variant="outline" size="sm" className="gap-2" onClick={loadRuns}>
            <RefreshCw className="h-4 w-4" /> {t('runs.retry', { defaultValue: 'Try again' })}
          </Button>
        </div>
      )}

      {/* No counts on a failed load: "0 awaiting approval" would be a claim, not a fact. */}
      {!loadError && (
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        <StatCard icon={Wallet} label={t('runs.statTotal')} value={String(stats.total)} loading={loading} />
        <StatCard icon={FileEdit} label={t('runs.statDraft', { defaultValue: 'Draft' })} value={String(stats.draft)} tone="default" loading={loading} />
        <StatCard icon={ClipboardCheck} label={t('runs.statAwaiting')} value={String(stats.awaiting)} tone="info" loading={loading} />
        <StatCard icon={ClipboardCheck} label={t('runs.statApproved', { defaultValue: 'Ready to lock' })} value={String(stats.approved)} tone="default" loading={loading} />
        <StatCard icon={Banknote} label={t('runs.statReady')} value={String(stats.ready)} tone="default" loading={loading} />
        <StatCard icon={CheckCircle2} label={t('runs.statPaid')} value={String(stats.paid)} tone="success" loading={loading} />
      </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <button
            type="button"
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={cn(
              'rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors',
              filter === f.value
                ? 'bg-charcoal text-white'
                : 'bg-card text-muted-foreground hover:bg-muted border border-border',
            )}
          >
            {t(f.labelKey)}
          </button>
        ))}

        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Select value={entityFilter} onValueChange={setEntityFilter}>
            <SelectTrigger className="h-9 w-48">
              <SelectValue placeholder={t('runs.filterAllCompanies', 'All companies')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('runs.filterAllCompanies', 'All companies')}</SelectItem>
              {entities.map((e) => (
                <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={periodFilter} onValueChange={setPeriodFilter}>
            <SelectTrigger className="h-9 w-44">
              <SelectValue placeholder={t('runs.filterAllPeriods', 'All periods')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('runs.filterAllPeriods', 'All periods')}</SelectItem>
              {periods.map((p) => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {filtersActive && (
            <button
              type="button"
              onClick={() => { setFilter('all'); setEntityFilter('all'); setPeriodFilter('all'); }}
              className="rounded-full border border-border px-3 py-1.5 text-sm font-medium text-muted-foreground hover:bg-muted"
            >
              {t('runs.filterClear', 'Clear')}
            </button>
          )}
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('runs.colPeriod')}</TableHead>
                <TableHead>{t('runs.colLegalEntity')}</TableHead>
                <TableHead>{t('runs.colCountry')}</TableHead>
                <TableHead className="text-right">{t('runs.colEmployees')}</TableHead>
                <TableHead className="text-right">{t('runs.colNetPay')}</TableHead>
                <TableHead>{t('runs.colStatus')}</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading &&
                Array.from({ length: 4 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={7}>
                      <Skeleton className="h-6 w-full" />
                    </TableCell>
                  </TableRow>
                ))}

              {!loading && !loadError && visible.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="py-12 text-center text-muted-foreground">
                    {!filtersActive ? t('runs.emptyAll') : t('runs.emptyFiltered', { filter })}
                  </TableCell>
                </TableRow>
              )}

              {!loading &&
                visible.map((run) => (
                  <TableRow
                    key={run.id}
                    onClick={() => navigate(`${base}/runs/${run.id}`)}
                    className="cursor-pointer"
                  >
                    <TableCell>
                      <div className="font-medium text-charcoal">
                        {run.periodLabel}
                        {run.label && (
                          <span className="ml-2 font-normal text-muted-foreground">· {run.label}</span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground capitalize">
                        {run.frequency}
                        {/* A row that says "cancelled" without saying why sends the
                            reader into the audit log for what this row knows. */}
                        {run.state === 'cancelled' && run.cancelReason && (
                          <span className="ml-1.5 normal-case">
                            · {t(`runs.cancelReason.${run.cancelReason}`, {
                              defaultValue: run.cancelReason.replace(/_/g, ' '),
                            })}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{run.legalEntityName}</TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                        <span aria-hidden>{COUNTRY_FLAG[run.country]}</span>
                        {COUNTRY_LABEL[run.country]}
                      </span>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {Number.isFinite(run.employeeCount) ? run.employeeCount : '—'}
                      {run.errored > 0 && (
                        <span className="ml-1 text-xs font-medium text-[#991B1B]">{t('runs.errCount', { count: run.errored })}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-medium tabular-nums text-charcoal">
                      {formatMoney(run.totals.net, run.currency)}
                    </TableCell>
                    <TableCell>
                      <StatusChip meta={RUN_STATE_META[run.state]} />
                    </TableCell>
                    <TableCell>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      </>
      )}
    </div>
  );
};
